import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AiAdminProvidersDto,
  AiAdminProvidersUpdate,
  AiAdminUsageDto,
  AiGenerateInput,
  AiGenerateResult,
  AiModelListDto,
  AiOrgSettingsUpdate,
  AiProvider,
  AiSettingsDto,
  AiSettingsTestInput,
  AiSettingsTestResultDto,
  AiStatusDto,
  AiUsageDto,
  AssistantReplyDto,
  AssistantThreadDto,
  AssistantThreadSummaryDto,
  BrainCompletenessDto,
  BrainPlaygroundInput,
  BrainSummaryDto,
  BusinessProfileDto,
  BusinessProfileUpdate,
  InsightDto,
  InsightListDto,
  InsightListQuery,
  InsightStatus,
  KnowledgeItemDto,
  KnowledgeItemInput,
  KnowledgeItemUpdate,
  KnowledgeListQuery,
  Paginated,
  PlaygroundResultDto,
} from "@digisubs/shared";
import { api } from "@/lib/api";

// Query keys and hooks of the AI layer: status, organization settings, models, usage, assistant threads, platform
// administration, Business Brain and Insights IA.

export const aiKeys = {
  all: ["ai"] as const,
  status: ["ai", "status"] as const,
  settings: ["ai", "settings"] as const,
  models: (provider: AiProvider) => ["ai", "models", provider] as const,
  usage: (period: string | undefined) => ["ai", "usage", period ?? "current"] as const,
  threads: ["ai", "threads"] as const,
  thread: (id: string) => ["ai", "thread", id] as const,
  adminProviders: ["ai", "admin", "providers"] as const,
  adminModels: (provider: AiProvider) => ["ai", "admin", "models", provider] as const,
  adminUsage: (period: string | undefined) => ["ai", "admin", "usage", period ?? "current"] as const,
};

export const brainKeys = {
  all: ["brain"] as const,
  profile: ["brain", "profile"] as const,
  knowledge: (q: Partial<KnowledgeListQuery>) => ["brain", "knowledge", q] as const,
  completeness: ["brain", "completeness"] as const,
  summary: ["brain", "summary"] as const,
};

export const insightKeys = {
  all: ["insights"] as const,
  list: (q: Partial<InsightListQuery>) => ["insights", "list", q] as const,
};

// ---------------------------------------------------------------------------------------------------------------
// Status, settings, models, usage

export function useAiStatus(enabled = true) {
  return useQuery({ queryKey: aiKeys.status, queryFn: () => api.get<AiStatusDto>("/ai/status"), enabled, staleTime: 30_000 });
}

export function useAiSettings(enabled = true) {
  return useQuery({ queryKey: aiKeys.settings, queryFn: () => api.get<AiSettingsDto>("/ai/settings"), enabled });
}

export function useAiModels(provider: AiProvider | null, enabled = true) {
  return useQuery({
    queryKey: aiKeys.models(provider ?? "anthropic"),
    queryFn: () => api.get<AiModelListDto>("/ai/models", { query: { provider: provider ?? "anthropic" } }),
    enabled: enabled && Boolean(provider),
    staleTime: 5 * 60_000,
  });
}

export function useAiUsage(period: string | undefined, enabled = true) {
  return useQuery({ queryKey: aiKeys.usage(period), queryFn: () => api.get<AiUsageDto>("/ai/usage", { query: { period } }), enabled, placeholderData: keepPreviousData });
}

export function useSaveAiSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AiOrgSettingsUpdate) => api.put<AiSettingsDto>("/ai/settings", input),
    onSuccess: (settings) => {
      qc.setQueryData(aiKeys.settings, settings);
      void qc.invalidateQueries({ queryKey: aiKeys.status });
      void qc.invalidateQueries({ queryKey: ["ai", "models"] });
    },
  });
}

export function useTestAiSettings() {
  return useMutation({ mutationFn: (input: AiSettingsTestInput) => api.post<AiSettingsTestResultDto>("/ai/settings/test", input) });
}

export function useUpdateMemberAiLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ membershipId, limit }: { membershipId: string; limit: number | null }) => api.patch(`/org/members/${membershipId}`, { aiMonthlyTokenLimit: limit }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ai", "usage"] }),
  });
}

export function useGenerateContent() {
  return useMutation({ mutationFn: (input: AiGenerateInput) => api.post<AiGenerateResult>("/ai/generate", input) });
}

// ---------------------------------------------------------------------------------------------------------------
// Assistant

export function useAssistantThreads(enabled = true) {
  return useQuery({ queryKey: aiKeys.threads, queryFn: () => api.get<{ items: AssistantThreadSummaryDto[] }>("/ai/assistant/threads"), enabled });
}

export function useAssistantThread(id: string | null) {
  return useQuery({ queryKey: aiKeys.thread(id ?? ""), queryFn: () => api.get<AssistantThreadDto>(`/ai/assistant/threads/${id}`), enabled: Boolean(id) });
}

export function useSendAssistantMessage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { threadId?: string | null; message: string }) =>
      api.post<AssistantReplyDto>("/ai/assistant/messages", { message: input.message, ...(input.threadId ? { threadId: input.threadId } : {}) }),
    onSuccess: (reply, input) => {
      // The reply carries everything needed: append it to the cached thread instead of refetching (no loading flash).
      const userMessage = { role: "user" as const, content: input.message, toolsUsed: [], createdAt: new Date(new Date(reply.message.createdAt).getTime() - 1).toISOString() };
      qc.setQueryData<AssistantThreadDto>(aiKeys.thread(reply.thread.id), (old) => ({
        id: reply.thread.id,
        title: reply.thread.title,
        createdAt: old?.createdAt ?? reply.message.createdAt,
        lastMessageAt: reply.thread.lastMessageAt,
        messages: [...(old?.messages ?? []), userMessage, reply.message],
      }));
      void qc.invalidateQueries({ queryKey: aiKeys.threads });
      void qc.invalidateQueries({ queryKey: aiKeys.status });
    },
  });
}

export function useDeleteThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/ai/assistant/threads/${id}`),
    onSuccess: (_res, id) => {
      qc.removeQueries({ queryKey: aiKeys.thread(id) });
      void qc.invalidateQueries({ queryKey: aiKeys.threads });
    },
  });
}

// ---------------------------------------------------------------------------------------------------------------
// Platform administration

export function useAdminProviders(enabled = true) {
  return useQuery({ queryKey: aiKeys.adminProviders, queryFn: () => api.get<AiAdminProvidersDto>("/ai/admin/providers"), enabled });
}

export function useAdminModels(provider: AiProvider | null, enabled = true) {
  return useQuery({
    queryKey: aiKeys.adminModels(provider ?? "anthropic"),
    queryFn: () => api.get<AiModelListDto>("/ai/admin/models", { query: { provider: provider ?? "anthropic" } }),
    enabled: enabled && Boolean(provider),
    staleTime: 5 * 60_000,
  });
}

export function useAdminUsage(period: string | undefined, enabled = true) {
  return useQuery({ queryKey: aiKeys.adminUsage(period), queryFn: () => api.get<AiAdminUsageDto>("/ai/admin/usage", { query: { period } }), enabled, placeholderData: keepPreviousData });
}

export function useSaveAdminProviders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AiAdminProvidersUpdate) => api.put<AiAdminProvidersDto>("/ai/admin/providers", input),
    onSuccess: (dto) => {
      qc.setQueryData(aiKeys.adminProviders, dto);
      void qc.invalidateQueries({ queryKey: ["ai", "admin", "models"] });
      void qc.invalidateQueries({ queryKey: aiKeys.settings });
      void qc.invalidateQueries({ queryKey: aiKeys.status });
    },
  });
}

export function useValidateAdminKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { provider: AiProvider; apiKey?: string }) =>
      api.post<{ valid: boolean; message: string; modelsCount: number; source: "candidate" | "db" | "env" }>("/ai/admin/providers/validate", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: aiKeys.adminProviders }),
  });
}

export function useSetOrgTokenLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, limit }: { orgId: string; limit: number | null }) => api.put(`/ai/admin/orgs/${orgId}/limit`, { monthlyTokenLimit: limit }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["ai", "admin", "usage"] }),
  });
}

// ---------------------------------------------------------------------------------------------------------------
// Business Brain

export function useBusinessProfile(enabled = true) {
  return useQuery({ queryKey: brainKeys.profile, queryFn: () => api.get<BusinessProfileDto>("/brain/profile"), enabled });
}

export function useSaveBusinessProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: BusinessProfileUpdate) => api.put<BusinessProfileDto>("/brain/profile", patch),
    onSuccess: (profile) => {
      qc.setQueryData(brainKeys.profile, profile);
      void qc.invalidateQueries({ queryKey: brainKeys.completeness });
      void qc.invalidateQueries({ queryKey: brainKeys.summary });
    },
  });
}

export function useKnowledge(q: Partial<KnowledgeListQuery>, enabled = true) {
  return useQuery({
    queryKey: brainKeys.knowledge(q),
    queryFn: () => api.get<Paginated<KnowledgeItemDto>>("/brain/knowledge", { query: q }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

function invalidateBrain(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["brain", "knowledge"] });
  void qc.invalidateQueries({ queryKey: brainKeys.completeness });
  void qc.invalidateQueries({ queryKey: brainKeys.summary });
}

export function useSaveKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: KnowledgeItemInput | KnowledgeItemUpdate }) =>
      id ? api.patch<KnowledgeItemDto>(`/brain/knowledge/${id}`, input) : api.post<KnowledgeItemDto>("/brain/knowledge", input),
    onSuccess: () => invalidateBrain(qc),
  });
}

export function useDeleteKnowledge() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.delete(`/brain/knowledge/${id}`), onSuccess: () => invalidateBrain(qc) });
}

export function useBrainCompleteness(enabled = true) {
  return useQuery({ queryKey: brainKeys.completeness, queryFn: () => api.get<BrainCompletenessDto>("/brain/completeness"), enabled });
}

export function useBrainSummary(enabled = true) {
  return useQuery({ queryKey: brainKeys.summary, queryFn: () => api.get<BrainSummaryDto>("/brain/summary"), enabled });
}

export function usePlayground() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BrainPlaygroundInput) => api.post<PlaygroundResultDto>("/brain/playground", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: aiKeys.status }),
  });
}

// ---------------------------------------------------------------------------------------------------------------
// Insights IA

export function useInsights(q: Partial<InsightListQuery>, enabled = true) {
  return useQuery({ queryKey: insightKeys.list(q), queryFn: () => api.get<InsightListDto>("/insights", { query: q }), enabled, placeholderData: keepPreviousData });
}

export function useInsightStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: InsightStatus }) => api.patch<InsightDto>(`/insights/${id}`, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: insightKeys.all }),
  });
}

export function useMarkAllInsightsRead() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api.post<{ updated: number }>("/insights/read-all"), onSuccess: () => void qc.invalidateQueries({ queryKey: insightKeys.all }) });
}

export function useRefreshInsights() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (includeSummary: boolean) => api.post<{ created: number; skipped: number; summary: string }>("/insights/refresh", { includeSummary }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: insightKeys.all }),
  });
}
